import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { getPresenceDataFromEvents, CONNECTION_TYPES } from '../../../components/utils/presenceHistoryUtils';

/**
 * PresenceHistoryGraph Component
 * 
 * Displays a presence history graph showing device connectivity patterns
 * for the selected date range.
 * - Blue dot: WiFi connection
 * - Orange dot: BLE connection
 * - Gray dot: Multiple connection types on same day
 * - Empty gray dot: Device never connected that day
 */

/**
 * Get dot color class based on connection type
 * @param {string} connectionType - The connection type (WIFI, BLE, BOTH)
 * @param {boolean} isConnected - Whether device was connected
 * @returns {string} Tailwind CSS class for dot color
 */
const getDotColorClass = (connectionType, isConnected) => {
  if (!isConnected) {
    return 'border-2 border-gray-400 bg-white';
  }
  switch (connectionType) {
    case CONNECTION_TYPES.WIFI:
      return 'bg-blue-500';
    case CONNECTION_TYPES.BLE:
      return 'bg-orange-500';
    case CONNECTION_TYPES.BOTH:
      return 'bg-gray-500';
    default:
      return 'bg-blue-500'; // Default to WiFi color if type unknown
  }
};

/**
 * Get tooltip text based on connection type
 * @param {string} connectionType - The connection type
 * @param {boolean} isConnected - Whether device was connected
 * @returns {string} Tooltip text
 */
const getConnectionTooltipText = (connectionType, isConnected) => {
  if (!isConnected) {
    return 'Not Connected';
  }
  switch (connectionType) {
    case CONNECTION_TYPES.WIFI:
      return 'Connected (WiFi)';
    case CONNECTION_TYPES.BLE:
      return 'Connected (BLE)';
    case CONNECTION_TYPES.BOTH:
      return 'Connected (WiFi + BLE)';
    default:
      return 'Connected';
  }
};

const PresenceHistoryGraph = ({ data, isLoading, startDate, endDate }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  // Generate presence data from connectivity events using the date range
  const presenceData = React.useMemo(() => {
    return getPresenceDataFromEvents(data, startDate, endDate);
  }, [data, startDate, endDate]);

  // Calculate the number of days in the range
  const totalDays = presenceData.length;
  
  // Calculate label interval based on range size
  const getLabelInterval = () => {
    if (totalDays <= 7) return 1; // Show every day
    if (totalDays <= 14) return 2; // Show every 2 days
    if (totalDays <= 31) return 7; // Show weekly
    return Math.ceil(totalDays / 5); // Show ~5 labels for longer ranges
  };

  // Format the title based on date range
  const getTitle = () => {
    const start = moment(startDate);
    const end = moment(endDate);
    return `Presence History (${start.format('MMM D')} - ${end.format('MMM D, YYYY')})`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700">Presence History</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(totalDays || 30, 30) }).map((_, idx) => (
              <div
                key={`skeleton-${idx}`}
                className="w-3 h-3 rounded-full bg-gray-200 animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!presenceData || presenceData.length === 0) {
    return (
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700">Presence History</h3>
        </div>
        <div className="flex items-center justify-center py-4 text-gray-500 text-sm">
          No data available for the selected date range
        </div>
      </div>
    );
  }

  const labelInterval = getLabelInterval();

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
      {/* Header with title and legend */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-700">{getTitle()}</h3>
        <div className="flex items-center space-x-4 text-xs text-gray-500">
          <div className="flex items-center space-x-1">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
            <span>WiFi</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
            <span>BLE</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-500" />
            <span>Multiple</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2.5 h-2.5 rounded-full border-2 border-gray-400 bg-white" />
            <span>Not Connected</span>
          </div>
        </div>
      </div>

      {/* Graph container with baseline - responsive with horizontal scroll on mobile */}
      <div className="relative pt-8 overflow-x-auto overflow-y-visible">
        <div className="min-w-[400px] px-6">
          {/* Baseline */}
          <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-300 -translate-y-1/2 z-0" style={{ top: 'calc(50% + 16px)' }} />
          
          {/* Dots */}
          <div className="relative flex items-center justify-between z-10">
            {presenceData.map((day, idx) => {
              // Determine tooltip alignment based on position
              const isFirst = idx < 3;
              const isLast = idx >= presenceData.length - 3;
              
              let tooltipStyle = { bottom: '100%', marginBottom: '8px' };
              let arrowStyle = { top: '100%' };
              
              if (isFirst) {
                // Align tooltip to the left for first dots
                tooltipStyle = { ...tooltipStyle, left: '0', transform: 'none' };
                arrowStyle = { ...arrowStyle, left: '6px', transform: 'none' };
              } else if (isLast) {
                // Align tooltip to the right for last dots
                tooltipStyle = { ...tooltipStyle, right: '0', transform: 'none' };
                arrowStyle = { ...arrowStyle, right: '6px', left: 'auto', transform: 'none' };
              } else {
                // Center tooltip for middle dots
                tooltipStyle = { ...tooltipStyle, left: '50%', transform: 'translateX(-50%)' };
                arrowStyle = { ...arrowStyle, left: '50%', transform: 'translateX(-50%)' };
              }
              
              return (
                <div
                  key={day.dateString}
                  className="relative flex flex-col items-center group"
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {/* Tooltip */}
                  {hoveredIndex === idx && (
                    <div 
                      className="absolute px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap z-50 pointer-events-none"
                      style={tooltipStyle}
                    >
                      {day.date.format('MMM D')}: {getConnectionTooltipText(day.connectionType, day.isConnected)}
                      <div 
                        className="absolute border-4 border-transparent border-t-gray-800" 
                        style={arrowStyle}
                      />
                    </div>
                  )}
                
                {/* Dot */}
                <div
                  className={`w-3 h-3 rounded-full cursor-pointer transition-transform hover:scale-125 ${
                    getDotColorClass(day.connectionType, day.isConnected)
                  }`}
                />
              </div>
              );
            })}
          </div>

          {/* Date labels - show first day, last day, and intermediate labels based on interval */}
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            {presenceData.map((day, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === totalDays - 1;
              const isIntervalLabel = idx > 0 && idx < totalDays - 1 && idx % labelInterval === 0;
              
              if (isFirst || isLast || isIntervalLabel) {
                return (
                  <span 
                    key={`label-${day.dateString}`} 
                    className={`text-center ${isFirst ? 'text-left' : ''} ${isLast ? 'text-right' : ''}`}
                    style={{ 
                      minWidth: '2.5rem',
                    }}
                  >
                    {day.date.format('MMM D')}
                  </span>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

PresenceHistoryGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      reason: PropTypes.string,
      updateAt: PropTypes.string,
      updatedAt: PropTypes.string,
      principalID: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object, // moment object
  ]),
  endDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object, // moment object
  ]),
};

PresenceHistoryGraph.defaultProps = {
  data: [],
  isLoading: false,
  startDate: null,
  endDate: null,
};

export default PresenceHistoryGraph;
